/**
 * Users feature — TanStack Query bindings.
 *
 * All hooks take the resolved tenant UUID, not the slug. Pages
 * derive it from the `/_authed/tenants/$slug` route context. URLs
 * are canonical path-scoped:
 *   /api/v1/tenants/{tenant_id}/users[...]
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import { partialContainmentFrom } from "./containment";
import {
  userAdoptionSchema,
  userDetailSchema,
  userPasskeysResponseSchema,
  type UserPasskeysResponse,
  userRefsResponseSchema,
  userSessionSchema,
  usersListResponseSchema,
  containAuthorityResponseSchema,
  type ContainAuthorityResponse,
  type CreateUserFormValues,
  type UserAdoption,
  type EditableUserAttribute,
  type UpdateUserStatusInput,
  type UserDetail,
  type UserListItem,
  type UserRef,
  type UserSession,
  userDevicesResponseSchema,
  type UserDevices,
  userPushTokensResponseSchema,
  type UserPushToken,
  type UsersListResponse,
  type UsersListSearch,
  userDeviceIdentitiesResponseSchema,
  type UserDeviceIdentity,
} from "./schemas";
import { z } from "zod";

const userSessionsResponseSchema = z.object({ items: z.array(userSessionSchema) });

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}`;
}

// ── Fetchers ──────────────────────────────────────────────────────────────

// Browse page size. The backend caps limit at 500 and defaults to 50; we pin a
// stable page size so Prev/Next pages are uniform. Search mode reuses it as the
// top-N cap (results are then a "top N of M — refine" set, not a paged list).
export const USERS_PAGE_SIZE = 50;

// Minimum query length before the backend runs a search. Mirrors `searchMinChars`
// in the Go repository: below this the server returns nothing without touching
// the DB (a 1–2 char ILIKE can't use the trigram index and would seq-scan the
// whole directory), so the UI must prompt "keep typing" rather than show "no
// results". Keep in sync with the backend constant.
export const USERS_SEARCH_MIN_CHARS = 3;

export async function fetchUsersList(
  tenantID: string,
  search: UsersListSearch,
): Promise<UsersListResponse> {
  const isSearch = (search.q ?? "").length > 0;
  // Browse mode paginates by keyset cursor (the last cursor in the stack);
  // search mode has no cursor pagination, so we never send one there.
  const cursors = search.cursors ?? [];
  const cursor = isSearch ? undefined : cursors[cursors.length - 1];
  const raw = await apiFetch<unknown>(`${base(tenantID)}/users`, {
    searchParams: {
      q: search.q,
      status: search.status,
      source: search.source,
      organization_id: search.organization_id,
      sort_by: search.sortBy,
      sort_order: search.sortOrder,
      limit: USERS_PAGE_SIZE,
      cursor,
    },
  });
  return usersListResponseSchema.parse(raw);
}

// The server caps one resolve batch (must match the backend maxResolveIDs) — larger selections are
// chunked so a big allowlist never 400s and falls back to raw ids.
const RESOLVE_BATCH = 500;

// fetchUserRefs batch-resolves user ids → display labels (POST /users/resolve), chunked at the server
// cap so ANY selection size resolves. The scalable way to label a known selection or a page of rows
// without loading "page 1 of everything".
export async function fetchUserRefs(tenantID: string, ids: readonly string[]): Promise<UserRef[]> {
  if (ids.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += RESOLVE_BATCH) {
    chunks.push(ids.slice(i, i + RESOLVE_BATCH) as string[]);
  }
  const results = await Promise.all(
    chunks.map((chunk) =>
      apiFetch<unknown>(`${base(tenantID)}/users/resolve`, { method: "POST", body: { ids: chunk } }).then(
        (raw) => userRefsResponseSchema.parse(raw).refs,
      ),
    ),
  );
  return results.flat();
}

export async function fetchUserDetail(
  tenantID: string,
  userId: string,
): Promise<UserDetail> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/users/${encodeURIComponent(userId)}`,
  );
  return userDetailSchema.parse(raw);
}

export async function fetchUserPasskeys(
  tenantID: string,
  userId: string,
): Promise<UserPasskeysResponse> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/users/${encodeURIComponent(userId)}/credentials`,
  );
  return userPasskeysResponseSchema.parse(raw);
}

export async function fetchUserDeviceIdentities(
  tenantID: string,
  userId: string,
): Promise<readonly UserDeviceIdentity[]> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/users/${encodeURIComponent(userId)}/device-identities`,
  );
  return userDeviceIdentitiesResponseSchema.parse(raw).devices;
}

export async function fetchUserSessions(
  tenantID: string,
  userId: string,
): Promise<readonly UserSession[]> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/users/${encodeURIComponent(userId)}/sessions`,
  );
  return userSessionsResponseSchema.parse(raw).items;
}

// ── Queries ───────────────────────────────────────────────────────────────

export function useUsersQuery(
  tenantID: string,
  search: UsersListSearch,
  options?: { enabled?: boolean },
) {
  return useQuery<UsersListResponse, ApiError>({
    queryKey: queryKeys.users.list(tenantID, search),
    queryFn: () => fetchUsersList(tenantID, search),
    enabled: tenantID.length > 0 && (options?.enabled ?? true),
    // Keep the current page visible while the next/prev page loads, so the
    // table doesn't flash to a spinner on each pager click.
    placeholderData: keepPreviousData,
  });
}

/**
 * useUserRefsQuery batch-resolves a set of user ids to display labels (id → {display_name, email})
 * for pickers/tables. It resolves the ids the caller ALREADY holds — never loading a page of all users
 * to seed labels (the seed-scan anti-pattern). Returns a Map for O(1) label lookup; disabled for an
 * empty id set. The key is the sorted id set, so the same selection caches regardless of order.
 */
export function useUserRefsQuery(tenantID: string, ids: readonly string[]) {
  return useQuery<Map<string, UserRef>, ApiError>({
    queryKey: queryKeys.users.refs(tenantID, ids),
    queryFn: async () => {
      const refs = await fetchUserRefs(tenantID, ids);
      return new Map(refs.map((r) => [r.id, r]));
    },
    enabled: tenantID.length > 0 && ids.length > 0,
    staleTime: 5 * 60 * 1000, // labels change rarely; avoid re-resolving on every open
  });
}

export function useUserQuery(tenantID: string, userId: string) {
  return useQuery<UserDetail, ApiError>({
    queryKey: queryKeys.users.detail(tenantID, userId),
    queryFn: () => fetchUserDetail(tenantID, userId),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

export function useUserPasskeysQuery(tenantID: string, userId: string) {
  return useQuery<UserPasskeysResponse, ApiError>({
    queryKey: queryKeys.users.passkeys(tenantID, userId),
    queryFn: () => fetchUserPasskeys(tenantID, userId),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

export function useUserDeviceIdentitiesQuery(tenantID: string, userId: string) {
  return useQuery<readonly UserDeviceIdentity[], ApiError>({
    queryKey: queryKeys.users.deviceIdentities(tenantID, userId),
    queryFn: () => fetchUserDeviceIdentities(tenantID, userId),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

export function useUserSessionsQuery(tenantID: string, userId: string) {
  return useQuery<readonly UserSession[], ApiError>({
    queryKey: queryKeys.users.sessions(tenantID, userId),
    queryFn: () => fetchUserSessions(tenantID, userId),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

/**
 * One person's devices: the browsers and clients PureID observed at their sign-in, and the Windows
 * workstations it issued them a logon certificate for. One read serves both lists.
 */
export function useUserDevicesQuery(tenantID: string, userId: string) {
  return useQuery<UserDevices, ApiError>({
    queryKey: queryKeys.users.devices(tenantID, userId),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/users/${encodeURIComponent(userId)}/devices`,
      );
      return userDevicesResponseSchema.parse(raw);
    },
    enabled: tenantID.length > 0 && userId.length > 0,
    refetchInterval: 60_000,
  });
}

/**
 * Revoke the trust marker on one of this person's observed devices. Sessions are not ended by this;
 * the next sign-in from that browser starts cold. Behind withStepUp; the server takes
 * devices.revoke and answers 404 for a device that is not this person's.
 */
export function useRevokeUserDeviceMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { deviceId: string }>({
    mutationFn: ({ deviceId }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `${base(tenantID)}/users/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}/revoke`,
          { method: "POST" },
        );
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.users.devices(tenantID, userId) }),
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

/**
 * Ends a person's record. Behind withStepUp: the server takes users.delete and
 * a fresh step-up. On success the detail cache is dropped rather than updated —
 * the page navigates away, and a tombstone is not a record to keep warm.
 */
export function useDeleteUserMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/users/${encodeURIComponent(userId)}`, { method: "DELETE" }),
      ),
    onSuccess: () => {
      qc.removeQueries({ queryKey: queryKeys.users.detail(tenantID, userId) });
      void qc.invalidateQueries({ queryKey: queryKeys.users.all(tenantID) });
    },
  });
}

export function useUpdateUserStatusMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<UserDetail, ApiError, UpdateUserStatusInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${base(tenantID)}/users/${encodeURIComponent(userId)}`,
          { method: "PATCH", body: input },
        ).then((raw) => userDetailSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.users.detail(tenantID, userId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.users.all(tenantID) });
    },
  });
}

/**
 * Whether an upstream may adopt this person as they are. Not polled: it moves
 * only when somebody enrolls, issues a pass or approves, and each of those
 * happens on this page or refetches it.
 */
export function useUserAdoptionQuery(tenantID: string, userId: string) {
  return useQuery<UserAdoption, ApiError>({
    queryKey: [...queryKeys.users.detail(tenantID, userId), "adoption"],
    queryFn: () =>
      apiFetch<unknown>(`${base(tenantID)}/users/${encodeURIComponent(userId)}/adoption`).then((raw) =>
        userAdoptionSchema.parse(raw),
      ),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

/**
 * Approves an upstream adopting this person with the credentials they hold now.
 * Step-up gated like every user write.
 */
export function useApproveAdoptionMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  // The credentials the administrator was shown ride with the approval. The
  // server refuses (409) when they are no longer the ones the person holds, so
  // an approval can never cover a passkey that appeared after the review.
  return useMutation<UserAdoption, ApiError, string[]>({
    mutationFn: (reviewedCredentialIDs) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/users/${encodeURIComponent(userId)}/adoption-approval`, {
          method: "POST",
          body: { reviewed_credential_ids: reviewedCredentialIDs },
        }).then((raw) => userAdoptionSchema.parse(raw)),
      ),
    onSuccess: (adoption) => {
      qc.setQueryData([...queryKeys.users.detail(tenantID, userId), "adoption"], adoption);
    },
  });
}

/**
 * Adds a person to a workforce tenant. Step-up gated — the server mounts the
 * create behind users.write and a fresh proof of presence. Empty optional names
 * are omitted rather than sent as empty strings.
 */
export function useCreateUserMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<UserDetail, ApiError, CreateUserFormValues>({
    mutationFn: (values) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/users`, {
          method: "POST",
          body: {
            display_name: values.display_name,
            email: values.email,
            ...(values.given_name ? { given_name: values.given_name } : {}),
            ...(values.family_name ? { family_name: values.family_name } : {}),
          },
        }).then((raw) => userDetailSchema.parse(raw)),
      ),
    onSuccess: (created) => {
      qc.setQueryData(queryKeys.users.detail(tenantID, created.id), created);
      void qc.invalidateQueries({ queryKey: queryKeys.users.all(tenantID) });
    },
  });
}

/**
 * Writes the attributes an administrator changed. Only changed fields are sent:
 * an omitted field is untouched on the server, and resending an unchanged one
 * would ask the directory authority gate about an attribute nobody edited.
 * Step-up gated — the server mounts this write behind users.write and a fresh
 * proof of presence.
 *
 * The resolved record is what the server holds after the write. An attribute
 * another source owns is dropped from the write rather than failing it, so the
 * caller compares what it sent with what came back.
 */
export function useUpdateUserAttributesMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<UserDetail, ApiError, Partial<Record<EditableUserAttribute, string>>>({
    mutationFn: (changes) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/users/${encodeURIComponent(userId)}`, {
          method: "PATCH",
          body: changes,
        }).then((raw) => userDetailSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.users.detail(tenantID, userId), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.users.all(tenantID) });
    },
  });
}

/**
 * containAuthority runs the contain-authority cascade. A full run is 200; a partial run is a 502
 * carrying the committed per-leg counts, which resolves here with that result (partial: true) so
 * the operator sees what already happened instead of a bare server error.
 */
export async function containAuthority(tenantID: string, userId: string): Promise<ContainAuthorityResponse> {
  try {
    return containAuthorityResponseSchema.parse(
      await apiFetch<unknown>(
        `${base(tenantID)}/users/${encodeURIComponent(userId)}/contain-authority`,
        { method: "POST" },
      ),
    );
  } catch (err) {
    const partial = partialContainmentFrom(err);
    if (partial) return partial;
    throw err;
  }
}

/**
 * useContainAuthorityMutation fires the operator-initiated owner-offboard cascade
 * (POST .../users/{id}/contain-authority): revokes the person's standing delegations and live
 * grants and suspends the non-agent principals they own — NOW, without waiting for the directory
 * ("HR says terminate now, SCIM runs at 6am"). It touches only what the person lent to machines,
 * never their own sign-in (disabling the person is the directory's job). Idempotent — a later
 * SCIM deprovision simply completes anything this missed. Step-up-gated
 * (agent_grants.kill_switch, the same class as the per-agent stop).
 */
export function useContainAuthorityMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<ContainAuthorityResponse, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        containAuthority(tenantID, userId),
      ),
    onSuccess: () => {
      // The blast lands on the AGENTS caches: standing delegations, grants, and suspended
      // principals all live under the agents key space.
      qc.invalidateQueries({ queryKey: queryKeys.agents.all });
      qc.invalidateQueries({ queryKey: queryKeys.users.detail(tenantID, userId) });
    },
  });
}

/**
 * Revoke one of this person's passkeys.
 *
 * `force` acknowledges the tenant's method floor: without it the server refuses
 * a revoke that would leave them unable to sign in, and the panel turns that
 * refusal into a second confirmation. See lib/method-floor-override.ts — the
 * flag must never be sent on a first attempt, or the operator is never warned.
 */
export function useRevokePasskeyMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { passkeyId: string; force?: boolean }>({
    mutationFn: ({ passkeyId, force }) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/users/${encodeURIComponent(userId)}/credentials/${encodeURIComponent(passkeyId)}/revoke`,
          { method: "POST", searchParams: force ? { force: "true" } : undefined },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.passkeys(tenantID, userId) });
      qc.invalidateQueries({ queryKey: queryKeys.users.detail(tenantID, userId) });
      qc.invalidateQueries({ queryKey: queryKeys.users.list(tenantID) });
    },
  });
}

/**
 * A user's registered mobile-authenticator installs.
 *
 * Polled at 60s to match the devices convention: install/uninstall is a
 * human-scale event, and an operator responding to a lost phone reloads
 * anyway.
 */
export function useUserPushTokensQuery(tenantID: string, userId: string) {
  return useQuery<readonly UserPushToken[], ApiError>({
    queryKey: queryKeys.users.pushTokens(tenantID, userId),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/users/${encodeURIComponent(userId)}/push-tokens`,
      );
      return userPushTokensResponseSchema.parse(raw).items;
    },
    enabled: tenantID.length > 0 && userId.length > 0,
    refetchInterval: 60_000,
  });
}

/**
 * Revokes one registered install — the lost-phone response.
 *
 * SCOPE: this stops sign-in approvals reaching the device. It does NOT sign
 * the device out (the app's bearer session survives) and does not touch the
 * user's passkey. The panel's confirm copy says so; a complete response also
 * revokes sessions from the Sessions tab.
 *
 * Behind withStepUp because the backend gates it on devices.revoke with a
 * 5-minute freshness window.
 */
export function useRevokePushTokenMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (pushTokenId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/users/${encodeURIComponent(userId)}/push-tokens/${encodeURIComponent(pushTokenId)}/revoke`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.pushTokens(tenantID, userId) });
    },
  });
}

export function useRevokeSessionMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (sessionId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/users/${encodeURIComponent(userId)}/sessions/${encodeURIComponent(sessionId)}/revoke`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.sessions(tenantID, userId) });
      qc.invalidateQueries({ queryKey: queryKeys.users.detail(tenantID, userId) });
    },
  });
}

export function useRevokeAllSessionsMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/users/${encodeURIComponent(userId)}/sessions/revoke-all`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.sessions(tenantID, userId) });
    },
  });
}

export { fetchUsersList as prefetchUsersList };
export type { UserListItem };
